import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import Layout from "../components/layout/Layout";
import { useControlCash } from "../store/controlCashStore";
import { useToast } from "../contexts/ToastContext";
import { getCategoryLabel } from "../utils/categoryUtils";
import { usePersistedFormState } from "../hooks/usePersistedFormState";

// ============================================================================
// CONSTANTES
// ============================================================================
const MONTHS = [
  { value: 1, label: "Enero" },
  { value: 2, label: "Febrero" },
  { value: 3, label: "Marzo" },
  { value: 4, label: "Abril" },
  { value: 5, label: "Mayo" },
  { value: 6, label: "Junio" },
  { value: 7, label: "Julio" },
  { value: 8, label: "Agosto" },
  { value: 9, label: "Septiembre" },
  { value: 10, label: "Octubre" },
  { value: 11, label: "Noviembre" },
  { value: 12, label: "Diciembre" }
];

const YEARS = Array.from({ length: 6 }, (_, i) => 2025 + i);

// ============================================================================
// HELPERS
// ============================================================================
const monthName = (monthNumber) => {
  const month = MONTHS.find(m => m.value === monthNumber);
  return month ? month.label : "";
};

const pad2 = (num) => {
  return String(num).padStart(2, "0");
};

const generateMonthName = (monthNumber, year) => {
  return `${monthName(monthNumber)} ${year}`;
};

// ============================================================================
// COMPONENTE: PageHeader
// ============================================================================
const PageHeader = ({ onBack }) => {
  return (
    <div className="mb-8">
      <button
        onClick={onBack}
        className="flex items-center gap-2 text-blue-600 hover:text-blue-700 font-medium mb-4 transition-colors group"
        aria-label="Volver al inicio"
      >
        <svg 
          className="w-5 h-5 transform group-hover:-translate-x-1 transition-transform" 
          fill="none" 
          stroke="currentColor" 
          viewBox="0 0 24 24"
        >
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10 19l-7-7m0 0l7-7m-7 7h18" />
        </svg>
        Volver
      </button>
      <h1 className="text-3xl md:text-4xl font-bold text-gray-800 mb-2">
        Nuevo Mes
      </h1>
      <p className="text-gray-600">
        Registra ingresos y genera tus gastos recurrentes automáticamente.
      </p>
    </div>
  );
};

// ============================================================================
// COMPONENTE: MonthSelector
// ============================================================================
const MonthSelector = ({ monthNumber, year, onChange, error }) => {
  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
        <svg className="w-6 h-6 text-blue-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
        </svg>
        Selecciona el mes
      </h2>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-4 mb-4">
        {/* Mes */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Mes <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <select
              value={monthNumber}
              onChange={(e) => onChange("monthNumber", parseInt(e.target.value))}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
            >
              {MONTHS.map((month) => (
                <option key={month.value} value={month.value}>
                  {month.label}
                </option>
              ))}
            </select>
            <svg 
              className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>

        {/* Año */}
        <div>
          <label className="block text-sm font-semibold text-gray-700 mb-2">
            Año <span className="text-red-500">*</span>
          </label>
          <div className="relative">
            <select
              value={year}
              onChange={(e) => onChange("year", parseInt(e.target.value))}
              className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:outline-none focus:ring-2 focus:border-blue-500 focus:ring-blue-200 transition-all appearance-none bg-white cursor-pointer"
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
            <svg 
              className="absolute right-4 top-1/2 -translate-y-1/2 w-5 h-5 text-gray-400 pointer-events-none" 
              fill="none" 
              stroke="currentColor" 
              viewBox="0 0 24 24"
            >
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 9l-7 7-7-7" />
            </svg>
          </div>
        </div>
      </div>

      {/* Preview del nombre */}
      <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border-2 border-blue-200 rounded-xl p-4">
        <p className="text-sm text-gray-600 mb-1">Nombre del mes:</p>
        <p className="text-2xl font-bold text-blue-700">
          {generateMonthName(monthNumber, year)}
        </p>
      </div>

      {error && (
        <p className="mt-3 text-sm text-red-600 flex items-center gap-1">
          <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
          </svg>
          {error}
        </p>
      )}
    </div>
  );
};

// ============================================================================
// COMPONENTE: IncomesSection
// ============================================================================
const IncomesSection = ({ incomes, onChange, errors, currency, formatUserAmount }) => {
  const toast = useToast();

  const addIncome = () => {
    onChange([...incomes, { id: Date.now(), name: "", amount: "" }]);
  };

  const removeIncome = (id) => {
    if (incomes.length === 1) {
      toast.warning("Debe haber al menos un ingreso");
      return;
    }
    onChange(incomes.filter(inc => inc.id !== id));
  };

  const updateIncome = (id, field, value) => {
    onChange(incomes.map(inc => 
      inc.id === id ? { ...inc, [field]: value } : inc
    ));
  };

  const totalIncome = incomes.reduce((sum, inc) => {
    const amount = parseFloat(inc.amount) || 0;
    return sum + amount;
  }, 0);

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-xl font-bold text-gray-800 flex items-center gap-2">
          <svg className="w-6 h-6 text-green-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          Ingresos del mes
        </h2>
        <button
          onClick={addIncome}
          className="px-4 py-2 bg-green-50 hover:bg-green-100 text-green-600 text-sm font-medium rounded-lg transition-colors flex items-center gap-1"
          aria-label="Agregar ingreso"
        >
          <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 4v16m8-8H4" />
          </svg>
          Agregar ingreso
        </button>
      </div>

      <div className="space-y-3 mb-4">
        {incomes.map((income, index) => (
          <div key={income.id} className="flex gap-3 items-start">
            <div className="flex-1 grid grid-cols-1 md:grid-cols-2 gap-3">
              {/* Nombre */}
              <div>
                <input
                  type="text"
                  value={income.name}
                  onChange={(e) => updateIncome(income.id, "name", e.target.value)}
                  placeholder="Ej: Sueldo, Freelance..."
                  className={`w-full px-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                    errors[`income_${income.id}_name`]
                      ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                      : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                  }`}
                />
                {errors[`income_${income.id}_name`] && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors[`income_${income.id}_name`]}
                  </p>
                )}
              </div>

              {/* Monto */}
              <div>
                <div className="relative">
                  <span className="absolute left-4 top-1/2 -translate-y-1/2 text-gray-500 font-semibold">
                    {currency}
                  </span>
                  <input
                    type="number"
                    step="0.01"
                    min="0"
                    value={income.amount}
                    onChange={(e) => updateIncome(income.id, "amount", e.target.value)}
                    placeholder="0.00"
                    className={`w-full pl-12 pr-4 py-3 border-2 rounded-xl focus:outline-none focus:ring-2 transition-all ${
                      errors[`income_${income.id}_amount`]
                        ? "border-red-300 focus:border-red-500 focus:ring-red-200"
                        : "border-gray-200 focus:border-blue-500 focus:ring-blue-200"
                    }`}
                  />
                </div>
                {errors[`income_${income.id}_amount`] && (
                  <p className="mt-1 text-xs text-red-600">
                    {errors[`income_${income.id}_amount`]}
                  </p>
                )}
              </div>
            </div>

            {/* Botón eliminar */}
            {incomes.length > 1 && (
              <button
                onClick={() => removeIncome(income.id)}
                className="p-3 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                aria-label="Eliminar ingreso"
              >
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                </svg>
              </button>
            )}
          </div>
        ))}
      </div>

      {/* Total */}
      <div className="bg-gradient-to-r from-green-50 to-emerald-50 border-2 border-green-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">Total de ingresos:</span>
          <span className="text-2xl font-bold text-green-700">
            {formatUserAmount(totalIncome)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE: GoalsAllocationSection
// ============================================================================
const GoalsAllocationSection = ({ goals, allocations, onChange, currency, formatAmount }) => {
  const updateAllocation = (goalId, amount) => {
    const num = parseFloat(amount) || 0;
    onChange(prev => {
      const filtered = prev.filter(a => a.goalId !== goalId);
      if (num > 0) {
        return [...filtered, { goalId, amount: num }];
      }
      return filtered;
    });
  };

  const getAllocation = (goalId) => {
    const a = allocations.find(x => x.goalId === goalId);
    return a ? String(a.amount) : "";
  };

  return (
    <div className="bg-white dark:bg-gray-800 rounded-2xl shadow-md p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-800 dark:text-gray-100 mb-4 flex items-center gap-2">
        <svg className="w-6 h-6 text-orange-500" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Asignar a metas de ahorro (opcional)
      </h2>
      <p className="text-sm text-gray-600 dark:text-gray-400 mb-4">
        Puedes aportar a tus metas al crear el mes. Deja en 0 para omitir.
      </p>
      <div className="space-y-3">
        {goals.map(goal => (
          <div key={goal.id} className="flex items-center gap-4 p-4 bg-gray-50 dark:bg-gray-700 rounded-xl">
            <div className="flex-1 min-w-0">
              <h4 className="font-semibold text-gray-800 dark:text-gray-100 truncate">{goal.name}</h4>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                Meta: {formatAmount(goal.targetAmount)}
              </p>
            </div>
            <div className="relative w-32 flex-shrink-0">
              <span className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-500 text-sm font-medium">{currency}</span>
              <input
                type="number"
                step="0.01"
                min="0"
                value={getAllocation(goal.id)}
                onChange={(e) => updateAllocation(goal.id, e.target.value)}
                placeholder="0"
                className="w-full pl-10 pr-3 py-2 border-2 border-gray-200 dark:border-gray-600 dark:bg-gray-600 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:border-orange-500"
              />
            </div>
          </div>
        ))}
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE: RecurringPreview
// ============================================================================
const RecurringPreview = ({ templates, onNavigateToRecurring, formatAmount, customCategories = [] }) => {
  const activeTemplates = templates.filter(t => t.active);
  const totalRecurring = activeTemplates.reduce((sum, t) => sum + t.amount, 0);

  if (activeTemplates.length === 0) {
    return (
      <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
        <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
          <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
          </svg>
          Gastos recurrentes
        </h2>

        <div className="bg-gray-50 border-2 border-gray-200 rounded-xl p-8 text-center">
          <svg className="w-16 h-16 mx-auto mb-4 text-gray-300" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M20 13V6a2 2 0 00-2-2H6a2 2 0 00-2 2v7m16 0v5a2 2 0 01-2 2H6a2 2 0 01-2-2v-5m16 0h-2.586a1 1 0 00-.707.293l-2.414 2.414a1 1 0 01-.707.293h-3.172a1 1 0 01-.707-.293l-2.414-2.414A1 1 0 006.586 13H4" />
          </svg>
          <h3 className="text-lg font-bold text-gray-800 mb-2">
            No hay gastos recurrentes activos
          </h3>
          <p className="text-gray-600 mb-4">
            No se generarán gastos automáticamente para este mes
          </p>
          <button
            onClick={onNavigateToRecurring}
            className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg transition-colors"
          >
            Ir a gastos recurrentes
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="bg-white rounded-2xl shadow-md p-6 mb-6">
      <h2 className="text-xl font-bold text-gray-800 mb-4 flex items-center gap-2">
        <svg className="w-6 h-6 text-orange-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15" />
        </svg>
        Se generarán estos gastos recurrentes
      </h2>

      <div className="bg-blue-50 border-2 border-blue-200 rounded-xl p-4 mb-4">
        <p className="text-sm text-blue-800 flex items-center gap-2">
          <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
            <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7-4a1 1 0 11-2 0 1 1 0 012 0zM9 9a1 1 0 000 2v3a1 1 0 001 1h1a1 1 0 100-2v-3a1 1 0 00-1-1H9z" clipRule="evenodd" />
          </svg>
          Se crearán automáticamente {activeTemplates.length} {activeTemplates.length === 1 ? "gasto pendiente" : "gastos pendientes"}
        </p>
      </div>

      <div className="space-y-3 mb-4">
        {activeTemplates.map((template) => (
          <div 
            key={template.id}
            className="flex items-center justify-between p-4 bg-gray-50 border border-gray-200 rounded-xl hover:bg-gray-100 transition-colors"
          >
            <div className="flex-1">
              <div className="flex items-center gap-3 mb-2">
                <h4 className="font-bold text-gray-800">{template.name}</h4>
                <span className="px-2 py-0.5 bg-purple-100 text-purple-700 text-xs font-medium rounded-full">
                  {getCategoryLabel(template.category, customCategories)}
                </span>
              </div>
              <div className="flex items-center gap-4 text-sm text-gray-600">
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" />
                  </svg>
                  Vence día {pad2(template.dueDay)}
                </span>
                <span className="flex items-center gap-1">
                  <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 17h5l-1.405-1.405A2.032 2.032 0 0118 14.158V11a6.002 6.002 0 00-4-5.659V5a2 2 0 10-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 11-6 0v-1m6 0H9" />
                  </svg>
                  {template.remindDaysBefore === 0 
                    ? "El mismo día" 
                    : `${template.remindDaysBefore} ${template.remindDaysBefore === 1 ? "día" : "días"} antes`
                  }
                </span>
              </div>
            </div>
            <div className="text-right">
              <p className="text-xl font-bold text-gray-900">
                {formatAmount(template.amount)}
              </p>
            </div>
          </div>
        ))}
      </div>

      {/* Total mensual estimado */}
      <div className="bg-gradient-to-r from-orange-50 to-red-50 border-2 border-orange-200 rounded-xl p-4">
        <div className="flex items-center justify-between">
          <span className="text-sm font-semibold text-gray-700">Total mensual estimado:</span>
          <span className="text-2xl font-bold text-orange-700">
            {formatAmount(totalRecurring)}
          </span>
        </div>
      </div>
    </div>
  );
};

// ============================================================================
// COMPONENTE PRINCIPAL: NewMonth
// ============================================================================
const NewMonth = () => {
  const navigate = useNavigate();
  const toast = useToast();
  const { state, dispatch, monthExists, toStorageAmount, toDisplayAmount, formatDisplayAmount, formatUserAmount } = useControlCash();
  const customCategories = state.customCategories || [];
  
  const currentDate = new Date();
  const recurringIncomeTemplates = Array.isArray(state.recurringIncomeTemplates) ? state.recurringIncomeTemplates : [];
  const activeIncomeTemplates = recurringIncomeTemplates.filter(t => t.active);
  const defaultIncomes = activeIncomeTemplates.length > 0
    ? activeIncomeTemplates.map(t => ({
        id: `inc_${t.id}_${Date.now()}`,
        name: t.name,
        amount: toDisplayAmount(t.amount).toString()
      }))
    : [{ id: Date.now(), name: "", amount: "" }];

  const [formState, setFormState, clearPersisted] = usePersistedFormState("newmonth", {
    monthNumber: currentDate.getMonth() + 1,
    year: currentDate.getFullYear(),
    incomes: defaultIncomes,
    goalAllocations: []
  });

  const { monthNumber, year, incomes, goalAllocations } = formState;
  const setIncomes = (updater) =>
    setFormState((prev) => ({ ...prev, incomes: typeof updater === "function" ? updater(prev.incomes) : updater }));
  const setGoalAllocations = (updater) =>
    setFormState((prev) => ({ ...prev, goalAllocations: typeof updater === "function" ? updater(prev.goalAllocations) : updater }));

  const [errors, setErrors] = useState({});
  const [isLoading, setIsLoading] = useState(false);

  const recurringTemplates = state.recurringTemplates;
  const activeGoals = (state.goals || []).filter(g => g.status === "ACTIVE");

  const handleMonthChange = (field, value) => {
    setFormState((prev) => ({ ...prev, [field]: value }));
    // Limpiar error de mes duplicado
    if (errors.monthExists) {
      setErrors(prev => ({ ...prev, monthExists: null }));
    }
  };

  const validate = () => {
    const newErrors = {};

    // Validar que el mes no exista
    if (monthExists(monthNumber, year)) {
      newErrors.monthExists = `El mes ${generateMonthName(monthNumber, year)} ya fue creado`;
    }

    // Validar ingresos
    let hasValidIncome = false;
    incomes.forEach(income => {
      if (!income.name.trim()) {
        newErrors[`income_${income.id}_name`] = "Nombre requerido";
      }
      if (!income.amount || parseFloat(income.amount) <= 0) {
        newErrors[`income_${income.id}_amount`] = "Monto debe ser mayor a 0";
      } else {
        hasValidIncome = true;
      }
    });

    if (!hasValidIncome) {
      newErrors.noValidIncomes = "Debe haber al menos un ingreso válido";
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleCreateMonth = async () => {
    if (!validate()) {
      return;
    }

    setIsLoading(true);
    const monthName = generateMonthName(monthNumber, year);

    try {
      // 1. Crear el mes en Supabase (obtener id real)
      const month = await dispatch({
        type: "CREATE_MONTH",
        payload: { name: monthName, monthNumber, year }
      });
      const monthId = month?.id;
      if (!monthId) {
        toast.error("Error al crear el mes");
        setIsLoading(false);
        return;
      }

      // 2. Guardar incomes del mes
      const validIncomes = incomes.filter(inc => inc.name.trim() && parseFloat(inc.amount) > 0);
      for (const inc of validIncomes) {
        await dispatch({
          type: "ADD_INCOME",
          payload: {
            monthId,
            name: inc.name.trim(),
            amount: toStorageAmount(parseFloat(inc.amount)),
            date: new Date().toISOString()
          }
        });
      }

      // 3. Aportes a metas de ahorro
      const allocationsToAdd = goalAllocations.filter(a => a.amount > 0);
      for (const { goalId, amount } of allocationsToAdd) {
        await dispatch({
          type: "ADD_CONTRIBUTION",
          payload: {
            goalId,
            amount: toStorageAmount(amount),
            date: new Date().toISOString().split("T")[0],
            note: `Aporte automático al crear mes`,
            monthId
          }
        });
      }

      // 4. Gastos recurrentes pendientes
      const activeTemplates = recurringTemplates.filter(t => t.active);
      for (const template of activeTemplates) {
        await dispatch({
          type: "ADD_EXPENSE",
          payload: {
            monthId,
            name: template.name,
            amount: template.amount,
            category: template.category,
            status: "PENDING",
            dueDay: template.dueDay,
            isFromRecurring: true,
            recurringTemplateId: template.id,
            remindDaysBefore: template.remindDaysBefore,
            date: new Date().toISOString(),
            paidAt: null
          }
        });
      }

      clearPersisted();
      navigate(`/month/${monthId}`);
    } catch (err) {
      console.error(err);
      toast.error(err?.message || "Error al guardar. Intenta de nuevo.");
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    clearPersisted();
    navigate("/");
  };

  return (
    <Layout>
      <div className="max-w-4xl mx-auto">
        {/* Header */}
        <PageHeader onBack={handleCancel} />

        {/* Selección del mes */}
        <MonthSelector
          monthNumber={monthNumber}
          year={year}
          onChange={handleMonthChange}
          error={errors.monthExists}
        />

        {/* Ingresos */}
        <IncomesSection
          incomes={incomes}
          onChange={setIncomes}
          errors={errors}
          currency={state.currencySymbol ?? state.currency}
          formatUserAmount={formatUserAmount}
        />

        {/* Info: ingresos recurrentes pre-cargados */}
        {activeIncomeTemplates.length > 0 && (
          <div className="bg-green-50 border-2 border-green-200 rounded-xl p-4 mb-6">
            <p className="text-sm text-green-800 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zm3.707-9.293a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
              </svg>
              {activeIncomeTemplates.length} {activeIncomeTemplates.length === 1 ? "ingreso recurrente" : "ingresos recurrentes"} agregados arriba. Puedes editarlos o{" "}
              <button type="button" onClick={() => navigate("/recurring-incomes")} className="text-green-700 font-semibold underline hover:no-underline">
                gestionar plantillas
              </button>
            </p>
          </div>
        )}

        {/* Asignar a metas de ahorro */}
        {activeGoals.length > 0 && (
          <GoalsAllocationSection
            goals={activeGoals}
            allocations={goalAllocations}
            onChange={setGoalAllocations}
            currency={state.currencySymbol ?? state.currency}
            formatAmount={formatDisplayAmount}
          />
        )}

        {/* Preview de gastos recurrentes */}
        <RecurringPreview
          templates={recurringTemplates}
          onNavigateToRecurring={() => navigate("/recurring")}
          customCategories={customCategories}
          formatAmount={formatDisplayAmount}
        />

        {/* Errores generales */}
        {errors.noValidIncomes && (
          <div className="bg-red-50 border-2 border-red-200 rounded-xl p-4 mb-6">
            <p className="text-red-700 flex items-center gap-2">
              <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                <path fillRule="evenodd" d="M18 10a8 8 0 11-16 0 8 8 0 0116 0zm-7 4a1 1 0 11-2 0 1 1 0 012 0zm-1-9a1 1 0 00-1 1v4a1 1 0 102 0V6a1 1 0 00-1-1z" clipRule="evenodd" />
              </svg>
              {errors.noValidIncomes}
            </p>
          </div>
        )}

        {/* Botones de acción */}
        <div className="flex flex-col-reverse sm:flex-row gap-3 pb-8">
          <button
            onClick={handleCancel}
            disabled={isLoading}
            className="w-full sm:w-auto px-6 py-3 border-2 border-gray-300 text-gray-700 font-semibold rounded-xl hover:bg-gray-50 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
            aria-label="Cancelar"
          >
            Cancelar
          </button>
          <button
            onClick={handleCreateMonth}
            disabled={isLoading}
            className="w-full sm:flex-1 px-6 py-3 bg-gradient-to-r from-green-500 to-green-600 hover:from-green-600 hover:to-green-700 text-white font-bold rounded-xl shadow-lg hover:shadow-xl transition-all disabled:opacity-50 disabled:cursor-not-allowed flex items-center justify-center gap-2"
            aria-label="Crear mes"
          >
            {isLoading ? (
              <>
                <svg className="animate-spin h-5 w-5 text-white" fill="none" viewBox="0 0 24 24">
                  <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4" />
                  <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z" />
                </svg>
                Creando mes...
              </>
            ) : (
              <>
                <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                </svg>
                Crear mes
              </>
            )}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default NewMonth;
